// Local VIN (Vehicle Identification Number) decoding — everything here is
// pure computation or a static reference table, no network involved.
//
// What's genuinely decodable from the VIN alone, standardized by 49 CFR 565 /
// ISO 3779, and implemented here:
//   - Format validity (17 characters, no I/O/Q)
//   - The check digit (position 9) — a real integrity check, not a guess
//   - Country of origin (from the first WMI character)
//   - A rough manufacturer guess, from a curated (NOT exhaustive) WMI table
//   - Candidate model year(s) (position 10 — see decodeModelYears for the
//     30-year-cycle ambiguity this necessarily carries)
//
// Exact make/model/trim/engine/plant is NOT derivable locally — that data
// isn't part of the VIN's public standard, only known to each manufacturer.
// The opt-in "full decode" (nhtsaVin.js) calls NHTSA's free public API for
// that, which is disclosed to the user before it's ever used.

const VALID_CHARS = /^[A-HJ-NPR-Z0-9]{17}$/; // excludes I, O, Q per spec

export function stripVin(input) {
  return String(input || '').trim().toUpperCase().replace(/\s+/g, '');
}

export function isValidVinFormat(vin) {
  return VALID_CHARS.test(stripVin(vin));
}

// ── check digit (position 9) ────────────────────────────────────────────────

const TRANSLITERATION = {
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
  J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9,
  S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
};

// Position 9 (the check digit itself) carries weight 0 so it cancels out of
// its own calculation.
const WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

function charValue(c) {
  if (c >= '0' && c <= '9') return Number(c);
  return TRANSLITERATION[c] ?? null;
}

/**
 * @param {string} vin 17-character VIN (well-formed)
 * @returns {string|null} the correct check digit ('0'-'9' or 'X'), or null if
 *   the VIN contains a character with no transliteration value
 */
export function computeCheckDigit(vin) {
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const value = charValue(vin[i]);
    if (value === null) return null;
    sum += value * WEIGHTS[i];
  }
  const remainder = sum % 11;
  return remainder === 10 ? 'X' : String(remainder);
}

/** @returns {boolean|null} null when the format is invalid, so callers never
 *  read an invalid VIN as "checksum failed" — those are different problems. */
export function validateCheckDigit(vin) {
  const v = stripVin(vin);
  if (!isValidVinFormat(v)) return null;
  const expected = computeCheckDigit(v);
  return expected !== null && expected === v[8];
}

// ── country of origin (WMI position 1, ISO 3780) ────────────────────────────

// First-character ranges covering the manufacturers LE most commonly
// encounters. ISO 3780 assigns finer-grained ranges by the first *two*
// characters in some regions (e.g. parts of "S" and "V"); this stays at
// single-character resolution, which is right far more often than it's wrong
// but is not authoritative — flagged as such in the UI.
const COUNTRY_BY_FIRST_CHAR = {
  1: 'United States', 4: 'United States', 5: 'United States',
  2: 'Canada',
  3: 'Mexico',
  6: 'Australia',
  9: 'Brazil / Argentina',
  J: 'Japan',
  K: 'South Korea',
  L: 'China',
  S: 'United Kingdom',
  V: 'France / Spain',
  W: 'Germany',
  Y: 'Sweden / Finland',
  Z: 'Italy',
};

export function decodeCountry(vin) {
  const v = stripVin(vin);
  if (!v[0]) return null;
  return COUNTRY_BY_FIRST_CHAR[v[0]] || null;
}

// ── manufacturer (full 3-character WMI) ──────────────────────────────────────
//
// A curated, NOT exhaustive, list of common WMI codes — enough to cover the
// large majority of vehicles a US investigator will run into. A miss here
// means "not in this list", never "not a real VIN".

const WMI_MANUFACTURER = {
  // Ford
  '1FA': 'Ford', '1FB': 'Ford', '1FC': 'Ford', '1FD': 'Ford', '1FM': 'Ford', '1FT': 'Ford',
  '2FA': 'Ford (Canada)', '2FM': 'Ford (Canada)', '2FT': 'Ford (Canada)',
  '3FA': 'Ford (Mexico)', '3FE': 'Ford (Mexico)',
  // GM
  '1G1': 'Chevrolet', '1G3': 'Oldsmobile', '1G4': 'Buick', '1G6': 'Cadillac',
  '1GC': 'Chevrolet (truck)', '1GT': 'GMC', '1GM': 'Pontiac',
  '2G1': 'Chevrolet (Canada)', '3G1': 'Chevrolet (Mexico)',
  // Stellantis / Chrysler
  '1C3': 'Chrysler', '1C4': 'Jeep/Chrysler', '1C6': 'Ram', '1B3': 'Dodge', '1B7': 'Dodge (truck)',
  '2C3': 'Chrysler (Canada)', '2C4': 'Chrysler (Canada)', '3C4': 'Chrysler (Mexico)', '3C6': 'Ram (Mexico)',
  // Honda
  '1HG': 'Honda', '2HG': 'Honda (Canada)', '5FN': 'Honda', 'JHM': 'Honda (Japan)', 'SHH': 'Honda (UK)',
  // Toyota
  '4T1': 'Toyota', '4T3': 'Toyota', '5TD': 'Toyota', '5TF': 'Toyota', 'JTD': 'Toyota (Japan)', 'JTE': 'Toyota (Japan)', 'JT2': 'Toyota (Japan)',
  // Nissan
  '1N4': 'Nissan', '1N6': 'Nissan (truck)', 'JN1': 'Nissan (Japan)', 'JN8': 'Nissan (Japan)',
  // Honda's rival Japanese makes
  'JM1': 'Mazda (Japan)', '1YV': 'Mazda (US-built)',
  'JF1': 'Subaru (Japan)', '4S3': 'Subaru', '4S4': 'Subaru',
  'JA3': 'Mitsubishi (Japan)', '4A3': 'Mitsubishi',
  // Hyundai/Kia
  'KMH': 'Hyundai', 'KM8': 'Hyundai', '5NP': 'Hyundai (US-built)',
  'KNA': 'Kia', 'KND': 'Kia', '5XY': 'Kia (US-built)',
  // German
  'WBA': 'BMW', 'WBS': 'BMW M', '4US': 'BMW (US-built)', '5UX': 'BMW (US-built)',
  'WDB': 'Mercedes-Benz', 'WDC': 'Mercedes-Benz', 'WDD': 'Mercedes-Benz', '4JG': 'Mercedes-Benz (US-built)',
  'WVW': 'Volkswagen', 'WV1': 'Volkswagen (commercial)', '3VW': 'Volkswagen (Mexico)', '1VW': 'Volkswagen (US-built)',
  'WAU': 'Audi', 'WA1': 'Audi',
  'WP0': 'Porsche', 'WP1': 'Porsche',
  // Tesla
  '5YJ': 'Tesla', '7SA': 'Tesla',
  // Volvo
  'YV1': 'Volvo', 'YV4': 'Volvo', '7JR': 'Volvo (US-built)',
  // Trucks / other
  '1XK': 'Kenworth', '1XP': 'Peterbilt', '4V4': 'Volvo Trucks', '1FU': 'Freightliner',
  '1HT': 'International', '3HA': 'Freightliner (Mexico)',
};

/** @returns {string|null} the manufacturer for a recognized WMI, or null if not in the table */
export function decodeManufacturer(vin) {
  const v = stripVin(vin);
  if (v.length < 3) return null;
  return WMI_MANUFACTURER[v.slice(0, 3)] || null;
}

// ── model year (position 10) ─────────────────────────────────────────────────

// The 30-year cycle, in order. I/O/Q/U/Z and the digit 0 are never used as
// year codes.
const YEAR_CYCLE = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K', 'L', 'M', 'N', 'P', 'R', 'S', 'T', 'V', 'W', 'X', 'Y', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

/**
 * Position 10 repeats every 30 years, so it alone is ambiguous — 'A' means
 * both 1980 and 2010. Position 7 conventionally disambiguates: on a
 * post-1981 (17-character) VIN, manufacturers have used a numeric position 7
 * for the 1980-2009 cycle and an alphabetic position 7 for 2010 onward. This
 * is a real, widely-used convention, but not a guarantee — so every possible
 * year is returned, most likely first, and the UI must not present this as
 * certain.
 *
 * @returns {{year: number, likely: boolean}[]} candidate years, most likely first
 */
export function decodeModelYears(vin) {
  const v = stripVin(vin);
  const code = v[9];
  const idx = YEAR_CYCLE.indexOf(code);
  if (idx === -1) return [];

  const cycleA = 1980 + idx;       // 1980-2009
  const cycleB = 2010 + idx;       // 2010-2039
  const pos7 = v[6];
  const pos7IsAlpha = /[A-Z]/.test(pos7 || '');

  const candidates = [cycleA, cycleB].map(year => ({
    year,
    likely: pos7IsAlpha ? year >= 2010 : year < 2010,
  }));

  return candidates.sort((a, b) => (b.likely ? 1 : 0) - (a.likely ? 1 : 0));
}

// ── combined local decode ────────────────────────────────────────────────────

/**
 * @param {string} rawVin
 * @returns {{
 *   vin: string, validFormat: boolean, checkDigitValid: boolean|null,
 *   country: string|null, manufacturer: string|null,
 *   modelYears: {year:number, likely:boolean}[],
 * }}
 */
export function decodeLocal(rawVin) {
  const vin = stripVin(rawVin);
  const validFormat = isValidVinFormat(vin);

  return {
    vin,
    validFormat,
    checkDigitValid: validFormat ? validateCheckDigit(vin) : null,
    country: validFormat ? decodeCountry(vin) : null,
    manufacturer: validFormat ? decodeManufacturer(vin) : null,
    modelYears: validFormat ? decodeModelYears(vin) : [],
  };
}
